import type { IntakeAnswers, IntakeQuestion } from "./types";

export function questionVisible(
  question: IntakeQuestion,
  answers: IntakeAnswers,
  masterAnswers: IntakeAnswers = answers,
): boolean {
  const condition = question.show_if;
  if (!condition) return true;
  const answer = (condition.scope === "master" ? masterAnswers : answers)[
    condition.key
  ];
  if (condition.operator === "includes_any") {
    const expected = Array.isArray(condition.value)
      ? condition.value
      : [condition.value];
    const selected = Array.isArray(answer) ? answer : [answer];
    return expected.some((value) => selected.includes(value as string));
  }
  if (condition.operator === "equals") return answer === condition.value;
  // An unrecognised condition must not silently hide a required question.
  return true;
}

export function applicableAnswers(
  questions: IntakeQuestion[],
  answers: IntakeAnswers,
  masterAnswers: IntakeAnswers = answers,
): IntakeAnswers {
  const visible: IntakeAnswers = {};
  for (const question of questions) {
    if (
      questionVisible(question, visible, masterAnswers) &&
      answers[question.key] !== undefined
    )
      visible[question.key] = answers[question.key];
  }
  return visible;
}

export function questionError(
  question: IntakeQuestion,
  answers: IntakeAnswers,
  masterAnswers: IntakeAnswers = answers,
): string | null {
  if (!questionVisible(question, answers, masterAnswers)) return null;
  const value = answers[question.key];
  const absent =
    value === undefined ||
    value === null ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0);
  if (absent) return question.required ? "Please answer this question." : null;
  if (question.type === "file") {
    const references = Array.isArray(value) ? value : [value];
    const limit = question.multiple
      ? Math.min(question.max_files ?? 10, 10)
      : 1;
    if (references.length > limit)
      return `Attach no more than ${limit} documents.`;
    if (
      references.some(
        (reference) =>
          typeof reference !== "string" ||
          !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(
            reference,
          ),
      )
    )
      return "Please upload the document again before continuing.";
  }
  if (question.type === "number") {
    if (typeof value !== "number" || !Number.isFinite(value))
      return "Enter a valid number.";
    if (
      question.key === "observation.height_inches" &&
      (value <= 2 || value >= 100)
    )
      return "Check your height in total inches. Values of 2 inches or less or 100 inches or more cannot be accepted.";
    if (question.key === "observation.current_weight_lbs" && value <= 0)
      return "Enter a weight greater than zero.";
    if (question.min != null && value < question.min)
      return `Enter ${question.min} or more${question.unit ? ` ${question.unit}` : ""}.`;
    if (question.max != null && value > question.max)
      return `Enter ${question.max} or less${question.unit ? ` ${question.unit}` : ""}.`;
  }
  if (
    question.type === "single_select" &&
    !question.options?.some((option) => option.value === value)
  )
    return "Choose an available answer.";
  if (question.type === "multi_select") {
    if (
      !Array.isArray(value) ||
      value.some(
        (item) => !question.options?.some((option) => option.value === item),
      )
    )
      return "Choose available answers.";
    if (
      value.length > 1 &&
      value.some((item) => question.exclusive_options?.includes(item))
    )
      return "Choose an exclusive answer by itself.";
  }
  if (
    question.type === "acknowledgement" &&
    question.required &&
    value !== true
  )
    return "Please confirm before continuing.";
  return null;
}

export function toggleSelection(
  question: IntakeQuestion,
  selected: string[],
  value: string,
): string[] {
  if (selected.includes(value))
    return selected.filter((item) => item !== value);
  if (question.exclusive_options?.includes(value)) return [value];
  return [
    ...selected.filter((item) => !question.exclusive_options?.includes(item)),
    value,
  ];
}
